import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  DollarSign, TrendingUp, CreditCard, Building2, BarChart3, ArrowUpRight
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";

const COLORS = ["#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#ef4444", "#06b6d4"];

const RevenueDashboard = () => {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchRevenue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/revenue`, { headers, withCredentials: true });
      setData(res.data);
    } catch { toast.error("Error al cargar datos de ingresos"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (user?.role === "super_admin") fetchRevenue(); }, [user, fetchRevenue]);

  if (user?.role !== "super_admin") return <Layout><p className="p-8">Acceso denegado</p></Layout>;

  const fmt = (n) => `$${(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 0 })}`;

  return (
    <Layout>
      <div className="p-4 md:p-8 space-y-6" data-testid="revenue-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard de Ingresos</h1>
            <p className="text-slate-500">Métricas de revenue y proyecciones</p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700"><DollarSign className="w-3 h-3 mr-1" />MRR</Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <KPICard label="Ingresos Totales" value={fmt(data.total_revenue)} icon={DollarSign} color="bg-emerald-500" sub="MXN" />
              <KPICard label="MRR" value={fmt(data.mrr)} icon={TrendingUp} color="bg-blue-500" sub="Mensual" />
              <KPICard label="ARR Proyectado" value={fmt(data.arr)} icon={ArrowUpRight} color="bg-violet-500" sub="Anual" />
              <KPICard label="Transacciones" value={data.total_transactions} icon={CreditCard} color="bg-amber-500" sub="Pagadas" />
              <KPICard label="Suscripciones" value={data.active_subscriptions} icon={Building2} color="bg-cyan-500" sub="Activas" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Monthly Revenue Chart */}
              <Card>
                <CardHeader><CardTitle className="text-base"><BarChart3 className="w-4 h-4 inline mr-2" />Ingresos Mensuales</CardTitle></CardHeader>
                <CardContent>
                  {data.monthly_data?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={data.monthly_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => [`$${v.toLocaleString("es-MX")} MXN`, "Ingresos"]} />
                        <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No hay datos de ingresos aún" />}
                </CardContent>
              </Card>

              {/* Plan Distribution */}
              <Card>
                <CardHeader><CardTitle className="text-base"><DollarSign className="w-4 h-4 inline mr-2" />Distribución por Plan</CardTitle></CardHeader>
                <CardContent>
                  {data.plan_breakdown?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={data.plan_breakdown} dataKey="revenue" nameKey="plan" cx="50%" cy="50%" outerRadius={100} label={({ plan, percent }) => `${plan} (${(percent * 100).toFixed(0)}%)`}>
                          {data.plan_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => `$${v.toLocaleString("es-MX")} MXN`} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <EmptyState text="No hay datos de planes aún" />}
                </CardContent>
              </Card>
            </div>

            {/* MRR Trend */}
            {data.monthly_data?.length > 1 && (
              <Card>
                <CardHeader><CardTitle className="text-base"><TrendingUp className="w-4 h-4 inline mr-2" />Tendencia MRR</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={data.monthly_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => [`$${v.toLocaleString("es-MX")} MXN`, "MRR"]} />
                      <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Recent Transactions */}
            <Card>
              <CardHeader><CardTitle className="text-base"><CreditCard className="w-4 h-4 inline mr-2" />Transacciones Recientes</CardTitle></CardHeader>
              <CardContent>
                {data.recent_transactions?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Empresa</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Monto</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Fecha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.recent_transactions.map((t, i) => (
                        <TableRow key={i}>
                          <TableCell>{t.company_id}</TableCell>
                          <TableCell><Badge variant="secondary">{t.plan_name}</Badge></TableCell>
                          <TableCell className="font-medium">{fmt(t.amount)}</TableCell>
                          <TableCell><Badge className={t.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{t.payment_status}</Badge></TableCell>
                          <TableCell className="text-sm text-slate-500">{new Date(t.paid_at || t.created_at).toLocaleDateString("es-MX")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <EmptyState text="No hay transacciones registradas" />}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

const KPICard = ({ label, value, icon: Icon, color, sub }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-xl font-bold truncate">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const EmptyState = ({ text }) => (
  <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm">{text}</div>
);

export default RevenueDashboard;
